import { getCanonicalOrigin } from "@/lib/getCanonicalOrigin";

const OAUTH_LOGIN_NEXT_PATH_KEY = "oauth_login_next_path";

const SHEETS_RECONNECT_AFTER_LOGIN_STORAGE_KEY =
  "signin_sheets_reconnect_after_login_path_v1";
const SHEETS_RECONNECT_INTENT_STORAGE_KEY = "signin_sheets_reconnect_pending_v1";

function hasSheetsReconnectClientStaging(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const search = window.location.search || "";
    if (new URLSearchParams(search).get("sheetsReconnectResume") === "1") {
      return true;
    }
    const oauthReturn = localStorage.getItem("oauth_return_path") || "";
    if (oauthReturn.includes("sheetsReconnectResume=1")) return true;
    const afterLogin = localStorage
      .getItem(SHEETS_RECONNECT_AFTER_LOGIN_STORAGE_KEY)
      ?.trim();
    if (afterLogin?.startsWith("/signin")) return true;
    if (
      localStorage.getItem(SHEETS_RECONNECT_INTENT_STORAGE_KEY)?.trim() ||
      sessionStorage.getItem(SHEETS_RECONNECT_INTENT_STORAGE_KEY)?.trim()
    ) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/** ค่า `callbackUrl` ชั่วคราวจาก deep-link (เช่น /go/billlin) ก่อนกดเชื่อมต่อบน /signin */
export const SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY =
  "signin_pending_callback_url_v1";

/** query + storage สำหรับ auto LINE หลังมาจาก /go/billlin บน LIFF */
export const SIGNIN_BILLIN_LIFF_AUTO_LINE_QUERY = "billin_liff_auto_line";

const SIGNIN_LIFF_AUTO_LINE_CONNECT_LOCAL_KEY =
  "signin_liff_auto_line_connect_v1";
const SIGNIN_LIFF_AUTO_LINE_CONNECT_SESSION_KEY =
  "signin_liff_auto_line_connect_v1";

export function billinLiffAutoLineSigninPath(): string {
  return `/signin?${SIGNIN_BILLIN_LIFF_AUTO_LINE_QUERY}=1`;
}

export function markSigninLiffAutoLineConnect(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_LOCAL_KEY, "1");
    sessionStorage.setItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_SESSION_KEY, "1");
  } catch {
    // ignore
  }
}

export function hasSigninLiffAutoLineConnectIntent(
  urlSearch: string | null,
): boolean {
  if (urlSearch !== null) {
    try {
      if (
        new URLSearchParams(urlSearch).get(SIGNIN_BILLIN_LIFF_AUTO_LINE_QUERY) ===
        "1"
      ) {
        return true;
      }
    } catch {
      // ignore
    }
  }
  if (typeof window === "undefined") return false;
  try {
    return (
      localStorage.getItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_LOCAL_KEY) === "1" ||
      sessionStorage.getItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_SESSION_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function clearSigninLiffAutoLineConnectIntent(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_LOCAL_KEY);
    sessionStorage.removeItem(SIGNIN_LIFF_AUTO_LINE_CONNECT_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function stripSigninBillinLiffAutoLineFromUrlBar(): void {
  if (typeof window === "undefined") return;
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has(SIGNIN_BILLIN_LIFF_AUTO_LINE_QUERY)) return;
    u.searchParams.delete(SIGNIN_BILLIN_LIFF_AUTO_LINE_QUERY);
    const qs = u.searchParams.toString();
    window.history.replaceState(
      null,
      "",
      `${u.pathname}${qs ? `?${qs}` : ""}${u.hash}`,
    );
  } catch {
    // ignore
  }
}

/** Normalize stored or URL `callbackUrl` (incl. legacy `/signin?callbackUrl=…`). */
export function normalizeSigninCallbackDestination(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "/") return "/";

  try {
    if (trimmed.startsWith("/signin")) {
      const q = trimmed.indexOf("?");
      if (q >= 0) {
        const inner = new URLSearchParams(trimmed.slice(q + 1)).get(
          "callbackUrl",
        );
        if (inner) return normalizeSigninCallbackDestination(inner);
      }
      return "/";
    }

    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const u = new URL(trimmed);
      if (typeof window !== "undefined" && u.origin === window.location.origin) {
        return normalizeSigninCallbackDestination(
          `${u.pathname}${u.search}${u.hash}`,
        );
      }
      return "/";
    }

    if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  } catch {
    // ignore
  }

  return "/";
}

export function persistSigninPendingCallbackUrl(returnPath: string): void {
  if (typeof window === "undefined") return;
  const dest = normalizeSigninCallbackDestination(returnPath);
  if (!shouldUseSigninPostAuthCallback(dest)) return;
  try {
    sessionStorage.setItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY, dest);
    localStorage.setItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY, dest);
  } catch {
    // ignore
  }
}

export function readSigninPendingCallbackUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw =
      sessionStorage.getItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY)?.trim() ||
      localStorage.getItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY)?.trim();
    if (!raw) return null;
    const dest = normalizeSigninCallbackDestination(raw);
    return shouldUseSigninPostAuthCallback(dest) ? dest : null;
  } catch {
    return null;
  }
}

export function clearSigninPendingCallbackUrl(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY);
    localStorage.removeItem(SIGNIN_PENDING_CALLBACK_URL_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** อ่าน `callbackUrl` จาก URL query หรือ sessionStorage (LIFF) */
export function resolveSigninCallbackPath(urlSearch: string | null): string {
  if (urlSearch !== null) {
    try {
      if (
        new URLSearchParams(urlSearch).get("sheetsReconnectResume") === "1" ||
        hasSheetsReconnectClientStaging()
      ) {
        return "/";
      }
    } catch {
      // ignore
    }
    const raw = new URLSearchParams(urlSearch).get("callbackUrl");
    if (raw) {
      const fromUrl = normalizeSigninCallbackDestination(raw);
      if (hasSheetsReconnectClientStaging()) return "/";
      if (shouldUseSigninPostAuthCallback(fromUrl)) return fromUrl;
      // Explicit `callbackUrl=/` — never substitute a stale Billin pending path.
      if (fromUrl === "/") return "/";
    }
  }
  if (hasSheetsReconnectClientStaging()) return "/";
  return readSigninPendingCallbackUrl() ?? "/";
}

/** ตอนกดเชื่อมต่อ — ย้าย pending → localStorage สำหรับ OAuth */
export function beginSigninOAuthWithCallback(callbackPathFromUi: string): string {
  if (hasSheetsReconnectClientStaging()) {
    clearSigninPendingCallbackUrl();
    clearSigninOAuthLoginNextPath();
    return "/";
  }
  const normalized = normalizeSigninCallbackDestination(callbackPathFromUi);
  const dest = shouldUseSigninPostAuthCallback(normalized)
    ? normalized
    : normalized === "/"
      ? "/"
      : (readSigninPendingCallbackUrl() ?? "/");
  clearSigninPendingCallbackUrl();
  if (shouldUseSigninPostAuthCallback(dest)) {
    persistSigninOAuthLoginNextPath(dest);
  }
  return dest;
}

export function resolveSigninOAuthCallbackDestination(
  explicitFromUrl: string | null | undefined,
  storedNext: string | null | undefined,
): string {
  if (hasSheetsReconnectClientStaging()) {
    return "/";
  }
  for (const raw of [
    explicitFromUrl,
    storedNext,
    readSigninPendingCallbackUrl(),
  ]) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const path = normalizeSigninCallbackDestination(raw);
    if (path !== "/") return path;
  }
  return "/";
}

export function shouldUseSigninPostAuthCallback(dest: string): boolean {
  return dest !== "/" && dest.startsWith("/") && !dest.startsWith("//");
}

export function readSigninOAuthLoginNextPath(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(OAUTH_LOGIN_NEXT_PATH_KEY)?.trim() || null;
  } catch {
    return null;
  }
}

export function clearSigninOAuthLoginNextPath(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(OAUTH_LOGIN_NEXT_PATH_KEY);
  } catch {
    // ignore
  }
}

export function persistSigninOAuthLoginNextPath(destination: string): void {
  if (typeof window === "undefined") return;
  const dest = normalizeSigninCallbackDestination(destination);
  if (!shouldUseSigninPostAuthCallback(dest)) return;
  try {
    localStorage.setItem(OAUTH_LOGIN_NEXT_PATH_KEY, dest);
  } catch {
    // ignore
  }
}

export function signinPathWithCallbackUrl(callbackPath: string): string {
  const dest = normalizeSigninCallbackDestination(callbackPath);
  if (!shouldUseSigninPostAuthCallback(dest)) return "/signin";
  return `/signin?callbackUrl=${encodeURIComponent(dest)}`;
}

export function navigateSigninPostAuthCallback(dest: string): void {
  const path = normalizeSigninCallbackDestination(dest);
  if (!shouldUseSigninPostAuthCallback(path)) return;
  const base = getCanonicalOrigin() || window.location.origin;
  window.location.replace(`${base}${path}`);
}
